import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';
import { Pencil1Icon, PersonIcon, PlusIcon, TrashIcon } from '@radix-ui/react-icons';

import { Chip, IconButton } from '@producer/ui-kit';

import { PagePlaceholder } from '../../../components/page-placeholder/page-placeholder';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { DropdownMenu, type Item } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { GroupHeader } from '../../../components/group-header/group-header';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { InlineEditableText } from '../../../components/editable-text/inline-editable-text';
import { SingleSelectDropdown } from '../../../components/select-dropdown';
import { RoleSelect } from '../../teams/components/role-select';
import { openModal } from '../../../core/modal/open-modal';
import {
  Table,
  TableHeader,
  THead,
  Cell,
  Row,
} from '../../../components/table/styled-shared-table';

import type { CrewMember } from '../../../app/entities/crew-member';
import type { CrewMemberInput } from '../stores/crew.slice';

import styles from './crew-list.module.scss';

const COLUMNS = ['Name', 'Role', 'Department', 'Team', 'Email', 'Phone'];

export type CrewGroupBy = 'department' | 'team' | 'role' | null;

type CrewListProps = {
  members: CrewMember[];
  groupBy?: CrewGroupBy;
  /**
   * Row actions are opt-in, and the actions column exists only when at least one is passed.
   *
   * This list is not exclusive to the screens that own crew records — it is presentational and
   * reusable, and a screen whose subject is something else (a shooting day's call times, say)
   * must not grow a Delete on every crew row just by rendering it. `Resources > Crew` and the
   * Crew step page pass these; nothing else should.
   */
  onEdit?: (member: CrewMember) => void;
  onDelete?: (member: CrewMember) => void;

  /**
   * Quick assign: the row menu's "Assign contact"/"Change contact" item, and — for an unfilled
   * position — the "Unassigned" chip itself, which becomes a control rather than decoration once
   * this is passed. Both open the same modal; this callback is how the two screens do that.
   */
  onAssign?: (member: CrewMember) => void;

  /**
   * Adds a crew member straight into a group, from that group's header row. Opt-in and only
   * rendered while grouping is active — an ungrouped list has no group to add to. `value` is the
   * group's raw value (e.g. a department name), or `''` for the "Ungrouped" bucket.
   */
  onAddToGroup?: (value: string) => void;

  /**
   * Offers a create action on the empty state, the way `Resources > Locations` does. Optional
   * because a screen that only reads crew (the shooting day) has nothing to offer here.
   */
  onCreate?: () => void;

  /**
   * Turns all six data columns into inline fields. Opt-in for the same reason the row actions
   * are: a screen that merely *displays* crew (the shooting day's call times) must not turn
   * every column into a writable field just by rendering this list.
   *
   * Called with ONE field per commit — never the whole row. The values here arrive already
   * RESOLVED from the server (a blank stored name displays as the contact's name, a blank stored
   * email as the contact's), so echoing untouched fields back would write those fallbacks in as
   * real stored overrides. A cleared field still sends `''` rather than being omitted: on a
   * PATCH an absent field means "leave it alone".
   *
   * The screen owns the write — this list stays presentational and does no fetching.
   */
  onUpdate?: (member: CrewMember, update: CrewMemberInput) => void;

  /**
   * The project's department/team pick-lists, for the inline pickers. Only read when `onUpdate`
   * is passed. Values already on a row are folded in regardless, so a row predating the
   * pick-lists — or created by migration 0007 — is never silently blanked by its own picker.
   */
  departments?: string[];
  teams?: string[];
};

/**
 * Presentational and controlled: no fetching, no filter state. The route passes already-filtered
 * members and hands the same filter to the export, so the two cannot disagree.
 */
const groupMembers = (members: CrewMember[], groupBy: CrewGroupBy) => {
  if (!groupBy) return [{ name: '', members }];

  const groups = new Map<string, CrewMember[]>();

  // Insertion order follows the members' existing order, so grouping never reshuffles people.
  for (const member of members) {
    const key = member[groupBy] || '';
    const existing = groups.get(key);
    if (existing) {
      existing.push(member);
    } else {
      groups.set(key, [member]);
    }
  }

  return [...groups.entries()].map(([name, grouped]) => ({ name, members: grouped }));
};

/** Mirrors `LocationList`'s empty-state copy, which is the pattern this follows. */
const EMPTY_DESCRIPTION =
  'Crew will be displayed here, you can add people by clicking on the "Add crew member" button';

/**
 * Reuses the webapp's existing row-menu idiom — `DropdownMenu` plus `openModal(DeleteDialog)` —
 * rather than introducing a second confirmation pattern. Delete is never immediate: the dialog
 * confirms first, and only then is `onDelete` called.
 */
const RowActions: React.FC<{
  member: CrewMember;
  onEdit?: (member: CrewMember) => void;
  onDelete?: (member: CrewMember) => void;
  onAssign?: (member: CrewMember) => void;
}> = ({ member, onEdit, onDelete, onAssign }) => {
  const items: Item[] = [
    ...(onAssign
      ? [
          {
            // Says what it does in context: an unfilled position is assigned, a filled one has
            // its contact changed — "Edit" already covers everything else about the row.
            title: member.isAssigned ? 'Change contact' : 'Assign contact',
            icon: <PersonIcon />,
            onSelect: () => onAssign(member),
          },
        ]
      : []),
    ...(onEdit ? [{ title: 'Edit', icon: <Pencil1Icon />, onSelect: () => onEdit(member) }] : []),
    ...(onDelete
      ? [
          {
            title: 'Delete',
            icon: <TrashIcon />,
            type: 'danger' as const,
            onSelect: () =>
              openModal(DeleteDialog, {
                title: 'Delete crew member',
                text: `Delete ${member.name || 'this position'}? This cannot be undone.`,
                onSubmit: () => onDelete(member),
              }),
          },
        ]
      : []),
  ];

  return <DropdownMenu items={items} />;
};

/**
 * Only shape validation, and only where a typo is silent: a malformed email looks fine in a cell
 * and fails much later, in the call sheet. Name and phone are deliberately unvalidated — crews
 * write "+33 6 12 34 56 78", "06.12.34.56.78" and "x421" in that column, and none of them is
 * wrong.
 */
const EMAIL_SCHEMA = yup.string().email('Please enter a valid email');

/**
 * The pick-list a row's picker actually shows: the project's list, plus every value already in
 * use on the list, plus anything typed this session. Folding the in-use values in is what keeps
 * a row whose department predates the pick-lists — or came from migration 0007 — from being
 * blanked the moment its own picker opens. De-duplicated, blanks dropped.
 */
const pickListOptions = (configured: string[], inUse: string[], created: string[]) =>
  [...new Set([...configured, ...inUse, ...created])]
    .filter(Boolean)
    .map((value) => ({ label: value, value }));

/**
 * The trigger the three pickers render instead of the kit's default. The default carries a
 * permanent border, which is right in a form and wrong in a table — see `.picker` in the
 * stylesheet. An empty cell falls back to the column's own name, which is both the kit's own
 * empty-state behaviour and what gives the control an accessible name with no value to read.
 */
const pickerTrigger = (column: string) => (label?: string) =>
  (
    <button type="button" className={styles['picker']}>
      {label || <span className={styles['pickerEmpty']}>{column}</span>}
    </button>
  );

export const CrewList: React.FC<CrewListProps> = observer(
  ({
    members,
    groupBy = null,
    onEdit,
    onDelete,
    onAssign,
    onAddToGroup,
    onCreate,
    onUpdate,
    departments = [],
    teams = [],
  }) => {
    // A department or team typed into one row's picker has to be choosable from every other row
    // straight away, not after a reload — so it is held here, for the whole list, rather than
    // per row. Persisting it onto the project's pick-lists is the screen's job (it owns the
    // project); this only keeps the pickers honest in the meantime.
    const [createdDepartments, setCreatedDepartments] = useState<string[]>([]);
    const [createdTeams, setCreatedTeams] = useState<string[]>([]);

    const hasActions = !!onEdit || !!onDelete || !!onAssign;
    const columnCount = COLUMNS.length + (hasActions ? 1 : 0);

    const departmentOptions = pickListOptions(
      departments,
      members.map((member) => member.department),
      createdDepartments,
    );
    const teamOptions = pickListOptions(
      teams,
      members.map((member) => member.team),
      createdTeams,
    );

    if (!members.length) {
      // Returned bare, exactly as `LocationList` does: `PagePlaceholder`'s own container is
      // `height: 100%`, so wrapping it in a height-less flex div collapsed it to a few pixels.
      // Two branches rather than a spread: `PagePlaceholderProps` is a discriminated union whose
      // no-action arm types `actionButton` as `never`, so an optionally-spread prop fits neither.
      return onCreate ? (
        <PagePlaceholder
          description={EMPTY_DESCRIPTION}
          actionButton={{ text: 'Add crew member', onClick: onCreate }}
        />
      ) : (
        <PagePlaceholder description={EMPTY_DESCRIPTION} />
      );
    }

    return (
      <Table>
        <TableHeader>
          <Row>
            {COLUMNS.map((column) => (
              <THead key={column}>{column}</THead>
            ))}
            {/* Deliberately unlabelled: a header for a menu column reads as noise. */}
            {hasActions && <THead className={styles['actions']} />}
          </Row>
        </TableHeader>

        {groupMembers(members, groupBy).map((group) => (
          <tbody key={group.name || 'all'}>
            {groupBy && (
              <Row>
                <THead colSpan={columnCount} className={styles['group']}>
                  <GroupHeader
                    title={group.name || 'Ungrouped'}
                    count={group.members.length}
                    action={
                      onAddToGroup && (
                        <Tooltip text="Add crew member">
                          <IconButton
                            icon={<PlusIcon />}
                            aria-label="Add crew member"
                            onClick={() => onAddToGroup(group.name)}
                          />
                        </Tooltip>
                      )
                    }
                  />
                </THead>
              </Row>
            )}

            {group.members.map((member) => (
              // Keyed by POSITION id, never contactId — two rows legitimately share one contact.
              <Row key={member._id} data-assigned={member.isAssigned}>
                <Cell className={styles['nameCell']} data-editable={!!onUpdate}>
                  {onUpdate ? (
                    <InlineEditableText
                      ariaLabel="Name"
                      value={member.name}
                      onSubmit={(name) => onUpdate(member, { name: name?.trim() ?? '' })}
                    />
                  ) : (
                    member.name
                  )}
                  {/* Its own element, not appended to the name string: an open slot needs to read
                      as a distinct badge at a glance in a list of dozens, not as a suffix on the
                      name. Do not fold this back into the name text — see crew-list.spec.tsx's
                      "shows an unfilled position with blank contact columns" test, which queries
                      the row rather than this text precisely so this can stay a real element.
                      When `onAssign` is wired up, the badge becomes a control — wrapped in its
                      own `<button>` rather than made clickable itself, since `Chip` has no
                      `onClick` and stays keyboard/screen-reader reachable either way (the button
                      carries its own `aria-label`, so it is announced as an action, not the
                      chip's raw "Unassigned" state text). */}
                  {!member.isAssigned &&
                    (onAssign ? (
                      <button
                        type="button"
                        className={styles['assignButton']}
                        onClick={() => onAssign(member)}
                        // Distinct wording from the row menu's "Assign contact"/"Change contact"
                        // item — both are always present together once `onAssign` is passed, and
                        // an identical accessible name on both would make them indistinguishable
                        // to anyone not using a mouse.
                        aria-label="Assign a contact"
                      >
                        <Chip className={styles['badge']}>Unassigned</Chip>
                      </button>
                    ) : (
                      <Chip className={styles['badge']}>Unassigned</Chip>
                    ))}
                </Cell>
                <Cell>
                  {onUpdate ? (
                    // The project-wide role list (a CSV of film crew roles) plus free text —
                    // the same picker the crew modal uses, with its label suppressed because the
                    // column header already names it.
                    <RoleSelect
                      hideLabel
                      trigger={pickerTrigger('Role')}
                      value={member.role}
                      onChange={(role) => onUpdate(member, { role: role.trim() })}
                    />
                  ) : (
                    member.role
                  )}
                </Cell>
                <Cell>
                  {onUpdate ? (
                    // Unlabelled on purpose: without `label` the kit's dropdown renders as a bare
                    // trigger line rather than a full form field, which is what fits a cell.
                    // `SingleSelectDropdown` rather than `InlineDropDown` because only this one
                    // takes `onCreate` — a department the project has never seen is a normal
                    // thing to type here.
                    <SingleSelectDropdown
                      title="Department"
                      trigger={pickerTrigger('Department')}
                      value={member.department}
                      options={departmentOptions}
                      onValueChange={(department) =>
                        onUpdate(member, { department: department?.trim() ?? '' })
                      }
                      onCreate={(typed) => {
                        // Trimmed the same way a chosen value is: untrimmed, "Camera " would
                        // become a second, indistinguishable entry in the project's pick-list.
                        const department = typed.trim();
                        if (!department) return;

                        setCreatedDepartments((created) =>
                          created.includes(department) ? created : [...created, department],
                        );
                        onUpdate(member, { department });
                      }}
                      placeholder="Choose from the list or use your own"
                    />
                  ) : (
                    member.department
                  )}
                </Cell>
                <Cell>
                  {onUpdate ? (
                    <SingleSelectDropdown
                      title="Team"
                      trigger={pickerTrigger('Team')}
                      value={member.team}
                      options={teamOptions}
                      onValueChange={(team) => onUpdate(member, { team: team?.trim() ?? '' })}
                      onCreate={(typed) => {
                        const team = typed.trim();
                        if (!team) return;

                        setCreatedTeams((created) =>
                          created.includes(team) ? created : [...created, team],
                        );
                        onUpdate(member, { team });
                      }}
                      placeholder="Choose from the list or use your own"
                    />
                  ) : (
                    member.team
                  )}
                </Cell>
                <Cell>
                  {onUpdate ? (
                    // Editable on every row, assigned or not. The server resolves a position's
                    // email from its own stored value first and only falls back to the linked
                    // contact (`resolve-crew-member.ts`), so what is typed here is a real
                    // override and will still be here on the next read.
                    <InlineEditableText
                      ariaLabel="Email"
                      value={member.email}
                      schema={EMAIL_SCHEMA}
                      onSubmit={(email) => onUpdate(member, { email: email?.trim() ?? '' })}
                    />
                  ) : (
                    member.email
                  )}
                </Cell>
                <Cell>
                  {onUpdate ? (
                    <InlineEditableText
                      ariaLabel="Phone"
                      value={member.phone}
                      onSubmit={(phone) => onUpdate(member, { phone: phone?.trim() ?? '' })}
                    />
                  ) : (
                    member.phone
                  )}
                </Cell>
                {hasActions && (
                  <Cell className={styles['actions']}>
                    <RowActions
                      member={member}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onAssign={onAssign}
                    />
                  </Cell>
                )}
              </Row>
            ))}
          </tbody>
        ))}
      </Table>
    );
  },
);
